/**
 * Fiskal Cloud Usage API
 * # Overview Retrieve usage reports for billing.  # Usage The export of reporting data can be triggered on tenant, organization or business premises level. In addition an overall usage report based on customer Identifier (from billing system) or for all customers can be requested.  Report is always based on a specific month.  ## Getting an authentication token  To be able to work with the API you need to get an authentication token to use with all subsequent API calls described below. Get the token via the [Oauth endpoint](https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token) of the Fiskal Cloud. Use client_id=df-api and grant_type=password for requesting a token. > The access token grants same access rights as the user has in the Fiskal Cloud Portal.  Example request to get token via curl: ``` curl -X POST \\ https://fiskal.cloud/auth/realms/df/protocol/openid-connect/token \\ -H \'Accept: application/json\' \\ -H \'Content-Type: application/x-www-form-urlencoded\' \\ -d \'username=<USER_EMAILADDRESS>&password=<USER_PASSWORD>&client_id=df-api&grant_type=password\' ``` Save the access_token entry of the response and pass it as bearer token in all subsequent API calls. Pass it as header parameter. ```\'Authorization: Bearer <ACCESS_TOKEN_FROM_AUTHENTICATION>\'```  For details on access tokens, please check general Oauth documentation like [Oauth.com](https://www.oauth.com/oauth2-servers/access-tokens/access-token-response/)  ## Tenant context All methods of this API work in the context of the logged in user\'s tenant account. For creating new elements you need to set the tenant id in the request. You can retrieve your tenant id by using the /tenants/tenant endpoint. It is fixed, so it can safely stored for later use.  ## Managed Tenant You can use a different tenant for operations if you have a permission to managed them. You can select managed tenant by sending `X-Managed-Tenant` header with `tenantId` of the managed tenant. All operations (creating, updating and getting) are done in the context of the managed tenant with this header.  ``` curl -X GET \\ -i \'https://fiskal.cloud/api/report/usage/{usageMonth}/tenant\' \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'X-Managed-Tenant: {{managedTenantId}}\' ```  ### Empty string `\"\"` behaviour change Currently our API converts all empty string `\"\"` values to null. This behaviour is not correspond to this OpenAPI specification. This behaviour **will be changed** in the near future and our API will not handle empty strings as nulls. Therefore your empty string values can be handled as invalid values. We strongly recommend not sending empty string values.  # Changelog  ## 1.7.0 * Remove FCR  ## 1.6.0 * New `UsageItem` added - `extErs`  ## 1.5.0 * New `UsageItem` added - `fccDcBusinessPremises`  ## 1.4.1 * Empty string `\"\"` as a value will not be handled as `null` announcement  ## 1.4.0 * FCC Data Center * UsageTypeEnum is extended for new types related to FCC Data Center  ## 1.2.0 * Deprecated API endpoints to get usage information and generate report by `customerIdentifier`. Try to get usage information or generate report by the contract ID.  ## 1.1.0 * Added contract endpoints  ## 1.0.1 * Added the volume contract type * `UsageItem` property `count` format is changed to `int64` 
 *
 * The version of the OpenAPI document: 1.6.0
 * Contact: info@deutsche-fiskal.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec, HttpContext 
        }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

// @ts-ignore
import { BusinessPremisesUsageContractDTO } from '../model/business-premises-usage-contract-dto';
// @ts-ignore
import { OrganizationUsageContractDTO } from '../model/organization-usage-contract-dto';
// @ts-ignore
import { TenantUsageContractDTO } from '../model/tenant-usage-contract-dto';

// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    UsageReportApiClientInterface,
    UsageBusinessPremisesRequestParams,
    UsageOrganizationRequestParams,
    UsageTenantRequestParams,
    UsageUserTenantRequestParams
} from './usage-report-api-client.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class UsageReportApiClient implements UsageReportApiClientInterface {

    protected basePath = 'https://fiskal.cloud/api/report';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string|string[], @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (Array.isArray(basePath) && basePath.length > 0) {
                basePath = basePath[0];
            }

            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    // @ts-ignore
    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Export installation master data per business premises.
     * This function will export usage data for a single business premises.  Returned usage data does not contain any detail (decomposition) for sub entities (erses). 
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public usageBusinessPremises(requestParameters: UsageBusinessPremisesRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<BusinessPremisesUsageContractDTO>;
    public usageBusinessPremises(requestParameters: UsageBusinessPremisesRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<BusinessPremisesUsageContractDTO>>;
    public usageBusinessPremises(requestParameters: UsageBusinessPremisesRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<BusinessPremisesUsageContractDTO>>;
    public usageBusinessPremises(requestParameters: UsageBusinessPremisesRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const businessPremisesId = requestParameters.businessPremisesId;
        if (businessPremisesId === null || businessPremisesId === undefined) {
            throw new Error('Required parameter businessPremisesId was null or undefined when calling usageBusinessPremises.');
        }
        const usageMonth = requestParameters.usageMonth;
        if (usageMonth === null || usageMonth === undefined) {
            throw new Error('Required parameter usageMonth was null or undefined when calling usageBusinessPremises.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/usage/${this.configuration.encodeParam({name: "usageMonth", value: usageMonth, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}/business-premises/${this.configuration.encodeParam({name: "businessPremisesId", value: businessPremisesId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<BusinessPremisesUsageContractDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Usage report per organization.
     * This function will export usage data for a single organization. Returned usage data does not contain any detail (decomposition) for sub entities (businessPremises). 
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public usageOrganization(requestParameters: UsageOrganizationRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<OrganizationUsageContractDTO>;
    public usageOrganization(requestParameters: UsageOrganizationRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<OrganizationUsageContractDTO>>;
    public usageOrganization(requestParameters: UsageOrganizationRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<OrganizationUsageContractDTO>>;
    public usageOrganization(requestParameters: UsageOrganizationRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const organizationId = requestParameters.organizationId;
        if (organizationId === null || organizationId === undefined) {
            throw new Error('Required parameter organizationId was null or undefined when calling usageOrganization.');
        }
        const usageMonth = requestParameters.usageMonth;
        if (usageMonth === null || usageMonth === undefined) {
            throw new Error('Required parameter usageMonth was null or undefined when calling usageOrganization.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/usage/${this.configuration.encodeParam({name: "usageMonth", value: usageMonth, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}/organizations/${this.configuration.encodeParam({name: "organizationId", value: organizationId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<OrganizationUsageContractDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Export usage data per tenant.
     * This function will export usage data for a selected tenant.  Returned usage data does not contain any detail (decomposition) for sub entities (organization). 
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public usageTenant(requestParameters: UsageTenantRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<TenantUsageContractDTO>;
    public usageTenant(requestParameters: UsageTenantRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<TenantUsageContractDTO>>;
    public usageTenant(requestParameters: UsageTenantRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<TenantUsageContractDTO>>;
    public usageTenant(requestParameters: UsageTenantRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const tenantId = requestParameters.tenantId;
        if (tenantId === null || tenantId === undefined) {
            throw new Error('Required parameter tenantId was null or undefined when calling usageTenant.');
        }
        const usageMonth = requestParameters.usageMonth;
        if (usageMonth === null || usageMonth === undefined) {
            throw new Error('Required parameter usageMonth was null or undefined when calling usageTenant.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/usage/${this.configuration.encodeParam({name: "usageMonth", value: usageMonth, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}/tenant/${this.configuration.encodeParam({name: "tenantId", value: tenantId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<TenantUsageContractDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Export usage data for current tenant.
     * This function will export usage data for the tenant of the current user. Returned usage data does not contain any detail (decomposition) for sub entities (organization). 
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public usageUserTenant(requestParameters: UsageUserTenantRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<TenantUsageContractDTO>;
    public usageUserTenant(requestParameters: UsageUserTenantRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<TenantUsageContractDTO>>;
    public usageUserTenant(requestParameters: UsageUserTenantRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<TenantUsageContractDTO>>;
    public usageUserTenant(requestParameters: UsageUserTenantRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const usageMonth = requestParameters.usageMonth;
        if (usageMonth === null || usageMonth === undefined) {
            throw new Error('Required parameter usageMonth was null or undefined when calling usageUserTenant.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/usage/${this.configuration.encodeParam({name: "usageMonth", value: usageMonth, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: undefined})}/tenant`;
        return this.httpClient.request<TenantUsageContractDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
